import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, ApiError, tokens } from './api'
import type { TokenPair, User } from './types'

interface AuthState {
  user: User | null
  loading: boolean
  /** Resolves to null when signed in, or to a challenge token when the
   *  account wants a second factor — hand it to `completeMfa` with the code. */
  signIn: (email: string, password: string) => Promise<string | null>
  completeMfa: (mfaToken: string, code: string) => Promise<void>
  signUp: (email: string, password: string, name?: string, inviteToken?: string) => Promise<void>
  signOut: () => void
  adopt: (pair: TokenPair) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [connectionError, setConnectionError] = useState(false)

  const load = useCallback(async () => {
    if (!tokens.access()) {
      setUser(null)
      setConnectionError(false)
      setLoading(false)
      return
    }
    try {
      const current = await api.me()
      if (tokens.access()) setUser(current)
      setConnectionError(false)
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        tokens.clear()
        setUser(null)
        setConnectionError(false)
      } else {
        setConnectionError(true)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!connectionError) return
    const retry = () => { void load() }
    const timer = window.setInterval(retry, 15_000)
    window.addEventListener('online', retry)
    return () => { window.clearInterval(timer); window.removeEventListener('online', retry) }
  }, [connectionError, load])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async signIn(email, password) {
        const result = await api.login(email, password)
        if ('mfa_required' in result) return result.mfa_token
        tokens.set(result)
        setUser(await api.me())
        return null
      },
      async completeMfa(mfaToken, code) {
        tokens.set(await api.mfa(mfaToken, code))
        setUser(await api.me())
      },
      async signUp(email, password, name, inviteToken) {
        tokens.set(await api.signup(email, password, name, inviteToken))
        setUser(await api.me())
      },
      signOut() {
        // Retire the refresh token on the server as well as forgetting it
        // here — otherwise a copy of localStorage keeps working for a month.
        // Best effort: the local sign-out must not wait on the network.
        const refresh = tokens.refresh()
        tokens.clear()
        setUser(null)
        if (refresh) void api.logout(refresh).catch(() => undefined)
      },
      // Used by the GitHub OAuth callback, which arrives with tokens in the
      // URL fragment rather than through a login form.
      async adopt(pair) {
        tokens.set(pair)
        setUser(await api.me())
      },
      refreshUser: load,
    }),
    [user, loading, load],
  )

  return <AuthContext.Provider value={value}>
    {connectionError && !user && tokens.access() ? (
      <main className="page" role="status">
        <h1>Reconnecting to TalariCloud</h1>
        <p>Your session is saved. We’ll reconnect automatically when the connection returns.</p>
        <button className="btn" onClick={() => void load()}>Retry now</button>
      </main>
    ) : children}
  </AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
